import React, { useEffect, useRef, useState } from 'react'
import { Circle, Close, DragIndicator } from '@mui/icons-material'
import {
  Collapse,
  Divider,
  IconButton,
  Slider,
  Tab,
  Tabs,
  TextField,
  useMediaQuery,
} from '@mui/material'
import { Question, useStores } from '@trivie/core'
import { QuestionHelper } from '@trivie/core/src/helpers/QuestionHelper'
import { QuestionType } from '@trivie/core/src/models/QuestionType'
import { AddQuestionsFromLibrary } from '@trivie/ui-web/components/add-question-from-library'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'
import { observer } from 'mobx-react-lite'
import { Instance, getNodeId } from 'mobx-state-tree'
import {
  ContentGenerationTab,
  a11yProps,
  useTabsStyles,
} from '../content-generation/quiz-generation/content-generation-tabs'
import CascadingHoverMenus from './nested-menu'
import { useHistory } from 'react-router-dom'
import UnsplashImageDialog from './unsplash-image-dialog'
import { ImageSelector } from '@trivie/ui-web/components/image-selector'
import { Switch } from '@trivie/ui-web/components/switch/switch'
import { ExpandMore } from '@trivie/ui-web/components/expand-button/expand-button'
import { ImageCropDialog } from '@trivie/ui-web/components/image-cropper/image-crop-dialog'
import { AddCardFromLibrary } from '@trivie/ui-web/components/add-card-from-library/add-card-from-library'

type ContentQuizSidebarProps = {
  quiz: any
  selectedQuestion: Instance<typeof Question> | null
  onSelectionChange: (selectedQuestion: Instance<typeof Question> | null) => void
  index?: number | null
  mercuryEnabled?: boolean
}

export const ContentQuizSidebar = observer((props: ContentQuizSidebarProps) => {
  const { index, quiz, onSelectionChange, mercuryEnabled } = props

  const { contentCreationStore } = useStores()

  const [hovered, setHovered] = useState<Instance<typeof Question> | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [showAddQuestionsFromLibrary, setShowAddQuestionsFromLibrary] = useState(false)
  const [showAddCardsFromLibrary, setShowAddCardsFromLibrary] = useState(false)
  const [grabbing, setGrabbing] = useState(false)
  const [draggingQuestionID, setDraggingQuestionID] = useState<null | number>(null)
  const [dropQuestionID, setDropQuestionID] = useState<null | number>(null)
  const [hoveredID, setHoveredID] = useState<null | number>(null)

  const [questionsExpanded, setQuestionsExpanded] = useState(true)
  const [domainExpanded, setDomainExpanded] = useState(false)

  const addQuestionRef = useRef(null)
  const history = useHistory<any>()

  useEffect(() => {
    // ensure one quiz is always selected
    if (!quiz?.selectedQuestion && quiz?.questions) {
      onSelectionChange(quiz?.questions[0])
    }
    if (history.location.state?.openAI) {
      onGenerate('quiz')
    }
  }, [])

  const handleAddQuestionsFromLibrary = () => {
    setShowAddQuestionsFromLibrary(true)
    setAnchorEl(null)
  }

  const handleAddCardsFromLibrary = () => {
    setShowAddCardsFromLibrary(true)
    setAnchorEl(null)
  }

  const handleAddQuestion = (type: Instance<typeof QuestionType>, init?) => {
    if (quiz) {
      const newQuestion = QuestionHelper.createNewQuestion(type)
      quiz.addQuestions([newQuestion])
      onSelectionChange(newQuestion)
      return newQuestion
    }
  }

  const tabsClasses = useTabsStyles()
  const [value, setValue] = React.useState(0)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  if (!quiz) {
    return null
  }

  const { settings } = quiz
  const { voice, setVoice, audience, setAudience } = settings

  const handleDrag = (question: Instance<typeof Question>) =>
    setDraggingQuestionID(getNodeId(question))

  const handleDragOver = (question: Instance<typeof Question>) =>
    setDropQuestionID(getNodeId(question))

  const handleDrop = (question: Instance<typeof Question>) => {
    setDropQuestionID(null)
    setGrabbing(false)

    const { questions } = quiz
    const dragQuestion = questions?.find((q) => getNodeId(q) === draggingQuestionID)
    const dropQuestion = questions?.find((q) => getNodeId(q) === getNodeId(question))

    if (dragQuestion && dropQuestion) {
      onSelectionChange(dragQuestion)
      quiz.reorderQuestions(
        questions?.indexOf(dragQuestion) ?? 0,
        questions?.indexOf(dropQuestion) ?? 0,
      )
    }
  }

  const getBackgroundColor = (q) => {
    if (dropQuestionID === getNodeId(q) && grabbing) {
      return color.shade10
    }
    return '#F8FAFD'
  }

  const onGenerate = (type) => {
    quiz?.set('stopGenerating', false)
    quiz?.set('showResourcePanel', true)

    if (type === 'quiz') {
      quiz.set('showSettings', true)
      quiz?.set('showQuestionPanel', false)
    } else if (quiz?.mercuryResource) {
      switch (type) {
        case 'TF':
          quiz?.generateQuestions(quiz?.mercuryResource.resource_id, 1, ['true_false'])
          return
        case 'MC':
          quiz?.generateQuestions(quiz?.mercuryResource.resource_id, 1, ['multiple_choice'])
          return
        case 'FITB':
          quiz?.generateQuestions(quiz?.mercuryResource.resource_id, 1, ['fill_in_the_blank'])
          return
        default:
          return
      }
    }
  }

  const [showUnsplashDialog, setShowUnsplashDialog] = useState(false)
  const [showCropper, setShowCropper] = useState(false)
  const [file, setFile] = useState<any>()

  const handleImage = (f: File | null) => {
    if (!f) {
      quiz.setImage(f)
    } else {
      setFile(f)
      setShowCropper(true)
    }
  }

  const mobile = useMediaQuery('(max-width:900px)')

  const tabSx = {
    // Dropdown container
    '&.Mui-selected': {
      color: '#000000',
    },
    '& > svg': {
      marginRight: '4px !important',
    },
    position: 'relative',
    padding: '6px 8px',
    overflow: 'hidden',
    // letterSpacing: '0px !important',
    maxWidth: '264px',
    minWidth: '0px',
    boxSizing: 'border-box',
    minHeight: '48px',
    textAlign: 'center',
    flexShrink: 0,
    whiteSpace: 'normal',
    textTransform: 'none',
    justifyContent: 'center',
  }

  return (
    <div style={ROOT}>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <div>
          <Tabs
            selectionFollowsFocus
            variant="standard"
            indicatorColor="primary"
            classes={{ ...tabsClasses }}
            value={value}
            style={{ paddingLeft: 16, marginBottom: 12 }}
            onChange={handleChange}
            aria-labelledby="a11y-tabs-automatic-label"
          >
            <Tab
              sx={tabSx}
              iconPosition="start"
              disableRipple
              label="Questions"
              style={{ marginRight: 8 }}
              {...a11yProps(0)}
            />
            <Tab
              sx={tabSx}
              iconPosition="start"
              disableRipple
              label="Settings"
              style={{ marginRight: 8 }}
              {...a11yProps(1)}
            />
          </Tabs>
        </div>
      </div>
      <div style={CONTAINER}>
        <ContentGenerationTab value={value} index={0}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(quiz.questions ?? ([] as Array<Instance<typeof Question>>))
              .filter((q) => !q.removing)
              .map((q, idx) => (
                <div
                  onClick={() => {
                    onSelectionChange(q)
                  }}
                  onMouseEnter={() => setHovered(q)}
                  onPointerLeave={() => setHovered(null)}
                  className="sidebar-question"
                  key={getNodeId(q)}
                  style={{
                    ...QUESTION_ROW,
                    backgroundColor: getBackgroundColor(q),
                    display: 'flex',
                    background:
                      q === quiz?.selectedQuestion
                        ? 'linear-gradient(81.39deg, #E4EBFF -188.61%, #ECE9FF 93.43%)'
                        : 'none',
                  }}
                  draggable={!quiz.shuffle && grabbing}
                  onDragOver={() => handleDragOver(q)}
                  onDrag={() => handleDrag(q)}
                  onDragEnd={(e) => setGrabbing(false)}
                  onDrop={() => handleDrop(q)}
                  onDragStart={(e) => {
                    e.dataTransfer.dropEffect = 'move'
                  }}
                >
                  <div style={QUESTION_ROW_CONTAINER}>
                    <div style={QUESTION_ROW_CONTENT}>
                      {hovered === q && !quiz.shuffle && (
                        <DragIndicator
                          onMouseLeave={() => setHoveredID(null)}
                          onMouseEnter={() => setHoveredID(getNodeId(q))}
                          onMouseDown={() => setGrabbing(true)}
                          onMouseUp={() => {
                            setGrabbing(false)
                            setDropQuestionID(null)
                          }}
                          style={{
                            ...HANDLE,
                            position: 'absolute',
                            left: -18,
                            fontSize: 16,
                            color: color.shade70,
                            // minHeight: 100,
                            cursor: grabbing ? 'grabbing' : 'grab',
                          }}
                        />
                      )}
                      <div style={QUESTION_ROW_TEXT}>
                        <Text
                          style={{ color: color.shade30, marginRight: 12 }}
                          variant="body1Medium"
                          text={`${idx + 1}.`}
                        />
                        <Text
                          style={{ ...QUESTION_TEXT, display: mobile ? '-webkit-box' : 'block' }}
                          text={
                            q.question_type === 'Card' && !q.card_name
                              ? 'New Info Card'
                              : q.card_name
                                ? q.card_name
                                : q.new && !q.text
                                  ? 'New Question'
                                  : q.text ?? ' '
                          }
                        />
                      </div>
                      {q?.generatingAll ? (
                        <Circle style={{ fontSize: 8, alignSelf: 'center' }} htmlColor="#194FF0" />
                      ) : q?.approved ? (
                        <Circle
                          style={{ fontSize: 8, alignSelf: 'center' }}
                          htmlColor={color.green}
                        />
                      ) : q?.allErrors.length && q?.touched ? (
                        <Circle
                          htmlColor={color.red}
                          style={{ fontSize: 8, alignSelf: 'center' }}
                        />
                      ) : (q?.allWarnings.length || !q?.approved) && q?.touched ? (
                        <Circle
                          htmlColor={color.yellow}
                          style={{ fontSize: 8, alignSelf: 'center' }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            {quiz?.generationCount > 0 && (
              <div id="generating-questions-loading" style={GENERATING_PANEL}>
                <IconButton
                  disabled
                  sx={{
                    animation: 'spin 3s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(-360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.65 12.35L7 11.175C6.91667 11.1417 6.875 11.0833 6.875 11C6.875 10.9167 6.91667 10.8583 7 10.825L9.65 9.625L10.825 6.975C10.8583 6.89167 10.9167 6.85 11 6.85C11.0833 6.85 11.1417 6.89167 11.175 6.975L12.375 9.625L15.025 10.825C15.1083 10.8583 15.15 10.9167 15.15 11C15.15 11.0833 15.1083 11.1417 15.025 11.175L12.375 12.35L11.175 15C11.1417 15.0833 11.0833 15.125 11 15.125C10.9167 15.125 10.8583 15.0833 10.825 15L9.65 12.35ZM11 22C9.13333 22 7.33333 21.45 5.6 20.35C3.86667 19.25 2.5 17.9917 1.5 16.575V19.25C1.5 19.4667 1.42917 19.6458 1.2875 19.7875C1.14583 19.9292 0.966667 20 0.75 20C0.533333 20 0.354167 19.9292 0.2125 19.7875C0.0708333 19.6458 0 19.4667 0 19.25V14.75C0 14.5333 0.0708333 14.3542 0.2125 14.2125C0.354167 14.0708 0.533333 14 0.75 14H5.25C5.46667 14 5.64583 14.0708 5.7875 14.2125C5.92917 14.3542 6 14.5333 6 14.75C6 14.9667 5.92917 15.1458 5.7875 15.2875C5.64583 15.4292 5.46667 15.5 5.25 15.5H2.575C3.425 16.7833 4.6375 17.9375 6.2125 18.9625C7.7875 19.9875 9.38333 20.5 11 20.5C13.5 20.5 15.6542 19.6583 17.4625 17.975C19.2708 16.2917 20.275 14.225 20.475 11.775C20.4917 11.5583 20.5708 11.375 20.7125 11.225C20.8542 11.075 21.0333 11 21.25 11C21.45 11 21.625 11.0708 21.775 11.2125C21.925 11.3542 21.9917 11.525 21.975 11.725C21.875 13.1583 21.5208 14.5 20.9125 15.75C20.3042 17 19.5083 18.0875 18.525 19.0125C17.5417 19.9375 16.4042 20.6667 15.1125 21.2C13.8208 21.7333 12.45 22 11 22ZM11 1.5C8.5 1.5 6.34583 2.34167 4.5375 4.025C2.72917 5.70833 1.725 7.775 1.525 10.225C1.50833 10.4417 1.42917 10.625 1.2875 10.775C1.14583 10.925 0.966667 11 0.75 11C0.55 11 0.375 10.9292 0.225 10.7875C0.075 10.6458 0.00833333 10.475 0.025 10.275C0.125 8.84167 0.479167 7.5 1.0875 6.25C1.69583 5 2.49167 3.9125 3.475 2.9875C4.45833 2.0625 5.59583 1.33333 6.8875 0.8C8.17917 0.266667 9.55 0 11 0C12.8667 0 14.6667 0.55 16.4 1.65C18.1333 2.75 19.5 4.00833 20.5 5.425V2.75C20.5 2.53333 20.5708 2.35417 20.7125 2.2125C20.8542 2.07083 21.0333 2 21.25 2C21.4667 2 21.6458 2.07083 21.7875 2.2125C21.9292 2.35417 22 2.53333 22 2.75V7.25C22 7.46667 21.9292 7.64583 21.7875 7.7875C21.6458 7.92917 21.4667 8 21.25 8H16.75C16.5333 8 16.3542 7.92917 16.2125 7.7875C16.0708 7.64583 16 7.46667 16 7.25C16 7.03333 16.0708 6.85417 16.2125 6.7125C16.3542 6.57083 16.5333 6.5 16.75 6.5H19.425C18.575 5.21667 17.3667 4.0625 15.8 3.0375C14.2333 2.0125 12.6333 1.5 11 1.5Z"
                      fill="url(#paint0_linear_2576_1057)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_2576_1057"
                        x1="3.43513"
                        y1="21.3045"
                        x2="26.0134"
                        y2="5.8725"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#FF00FF" />
                        <stop offset="0.46" stopColor="#9116F9" />
                        <stop offset="1" stopColor="#3199FF" />
                      </linearGradient>
                    </defs>
                  </svg>
                </IconButton>
                <Text
                  text={`Generating ${quiz?.generationCount} Question${
                    quiz?.generationCount > 1 ? 's' : ''
                  }...`}
                  style={{ fontWeight: 700, fontSize: 14 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    quiz.set('stopGenerating', true)
                    quiz.set('generationCount', 0)
                    quiz.setGeneratingQuestions(false)
                  }}
                >
                  <Close htmlColor={color.shade50} />
                </IconButton>
              </div>
            )}
          </div>
        </ContentGenerationTab>
        <ContentGenerationTab value={value} index={1}>
          <>
            <div style={{ overflowY: 'auto', overflowX: 'hidden' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingRight: 16,
                  paddingLeft: 16,
                }}
              >
                <Text
                  variant="body1Medium"
                  text="Cover Photo"
                  style={{ ...SECTION_HEADER, marginBottom: 12 }}
                />
                <ImageSelector
                  compact
                  fullWidth
                  onUnsplashClick={() => {
                    setShowUnsplashDialog(true)
                  }}
                  pickerStyle={{ height: 150 }}
                  imageSrc={quiz.image}
                  file={quiz.imageFile}
                  onChange={handleImage}
                />
              </div>
              {showCropper && (
                <ImageCropDialog
                  file={file}
                  open={showCropper}
                  onConfirm={(img: File) => {
                    quiz.setImage(img)
                    setShowCropper(false)
                  }}
                  onClose={() => {
                    quiz.setImage(null)
                    setShowCropper(false)
                  }}
                />
              )}
              {showUnsplashDialog && (
                <UnsplashImageDialog
                  onChangeImage={handleImage}
                  handleImageDialogClose={() => setShowUnsplashDialog(false)}
                  open={showUnsplashDialog}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20,
                  paddingRight: 16,
                  paddingLeft: 16,
                }}
              >
                <Text variant="body1Medium" text="Quiz Summary" style={SECTION_HEADER} />
                <TextField
                  multiline
                  placeholder="Enter a brief summary of your quiz for your learners"
                  variant="standard"
                  value={quiz?.description}
                  onChange={(e) => {
                    quiz?.set('description', e.target.value)
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                  paddingRight: 16,
                  paddingLeft: 16,
                }}
              >
                <Text variant="body1Medium" text="Questions" style={SECTION_HEADER} />
                <ExpandMore
                  expand={questionsExpanded}
                  onClick={() => setQuestionsExpanded(!questionsExpanded)}
                />
              </div>
              <Collapse
                style={{
                  marginBottom: questionsExpanded ? 24 : 0,
                  paddingRight: 16,
                  paddingLeft: 16,
                }}
                in={questionsExpanded}
                timeout="auto"
              >
                <div style={{ ...SWITCH_ROW, marginBottom: 0 }}>
                  <Text
                    style={{ color: color.shade70 }}
                    variant="body1Medium"
                    text="Shuffle Questions"
                  />
                  <Switch
                    color="success"
                    disabled={Boolean(quiz.hasInfoCards)}
                    checked={Boolean(quiz.shuffle)}
                    onChange={(e) => {
                      quiz.setShuffleOrder(e.target.checked)
                    }}
                    size="small"
                  />
                </div>
              </Collapse>
              <Divider style={{ marginBottom: 12, marginRight: 16, marginLeft: 16 }} />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                  paddingRight: 16,
                  paddingLeft: 16,
                }}
              >
                <div style={{ flexDirection: 'row', display: 'flex' }}>
                  <Text variant="body1Medium" text="Style" style={SECTION_HEADER} />
                  <Text variant="body1Medium" text="(Optional)" style={OPTIONAL_TEXT} />
                </div>
                <ExpandMore
                  expand={domainExpanded}
                  onClick={() => {
                    setDomainExpanded(!domainExpanded)
                  }}
                />
              </div>
              {mercuryEnabled && (
                <Collapse
                  style={{
                    paddingRight: 16,
                    paddingLeft: 16,
                    marginBottom: domainExpanded ? 24 : 0,
                  }}
                  in={domainExpanded}
                  timeout="auto"
                >
                  <div
                    style={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 10,
                    }}
                  >
                    <Text variant="body1" text="Writing Style:" style={LABEL} />
                    <TextField
                      size="small"
                      variant="outlined"
                      onChange={(e) => setVoice(e.target.value)}
                      inputProps={{
                        style: { backgroundColor: '#fff' },
                      }}
                      style={{ height: 33, width: 250 }}
                      value={voice}
                    />
                  </div>
                  <div
                    style={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <Text variant="body1" text="Audience:" style={LABEL} />
                    <TextField
                      size="small"
                      variant="outlined"
                      onChange={(e) => setAudience(e.target.value)}
                      inputProps={{
                        style: { backgroundColor: '#fff' },
                      }}
                      style={{ height: 33, width: 250 }}
                      value={audience}
                    />
                  </div>
                  <div
                    style={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text variant="body1" text="Creativity:" style={LABEL} />
                    <div
                      style={{
                        width: 250,
                        position: 'relative',
                        top: 8,
                        flexDirection: 'column',
                        display: 'flex',
                      }}
                    >
                      <Slider
                        size="small"
                        defaultValue={0}
                        max={100}
                        sx={{ width: 182, left: 5 }}
                        aria-label="Small"
                        valueLabelDisplay="auto"
                      />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text
                          style={{ color: color.shade60 }}
                          variant="body2"
                          text="Less Creative"
                        />
                        <Text
                          style={{ color: color.shade60 }}
                          variant="body2"
                          text="More Creative"
                        />
                      </div>
                    </div>
                  </div>
                </Collapse>
              )}
            </div>
          </>
        </ContentGenerationTab>
      </div>
      <div style={FOOTER} ref={addQuestionRef}>
        <div style={{ display: 'flex', flexDirection: 'column', padding: 24, width: '100%' }}>
          {value === 0 && (
            <CascadingHoverMenus
              disabled={!quiz?.mercuryResource || !quiz?.selectedDocument}
              mercuryEnabled={mercuryEnabled}
              onGenerate={onGenerate}
              quiz={quiz}
              onImportCard={handleAddCardsFromLibrary}
              onImport={handleAddQuestionsFromLibrary}
              onAdd={(qType) => {
                if (qType === 'Card') {
                  const newQuestion = handleAddQuestion('Multiple Choice')
                  quiz?.setShuffle(false)
                  quiz?.set('keep_item_order', true)
                  newQuestion?.setQuestionType('Card')
                } else {
                  handleAddQuestion('Multiple Choice')
                }
              }}
            />
          )}
        </div>
      </div>
      {showAddQuestionsFromLibrary && (
        <AddQuestionsFromLibrary
          existing={quiz.questions}
          open={showAddQuestionsFromLibrary}
          onClose={(addedQuestions: any) => {
            if (addedQuestions?.length !== 0) {
              quiz.addQuestions(QuestionHelper.objectsToQuestions(addedQuestions))
              onSelectionChange(quiz.questions[quiz.questions.indexOf(quiz?.selectedQuestion) + 1])
            }
            setShowAddQuestionsFromLibrary(false)
          }}
        />
      )}
      {showAddCardsFromLibrary && (
        <AddCardFromLibrary
          open={showAddCardsFromLibrary}
          onClose={(addedCards: any) => {
            if (addedCards?.length !== 0) {
              const cards = addedCards?.map((c) => {
                return QuestionHelper.createNewQuestion('Card', {
                  card_name: c.card_name,
                  card_id: c.id,
                  card_json: c.card_json,
                })
              })
              quiz.addCards(cards)
              quiz?.setShuffle(false)
              quiz?.set('keep_item_order', true)
              quiz?.addAlert({
                id: Math.random(),
                text: 'Shuffle has been disabled. Quizzes that contain info cards cannot be shuffled.',
                severity: 'warning',
                show: Date.now(),
                unique: true,
              })
              onSelectionChange(quiz.questions[quiz.questions?.length - 1])
            }
            setShowAddCardsFromLibrary(false)
          }}
        />
      )}
    </div>
  )
})

const ROOT: React.CSSProperties = {
  background: 'rgba(248, 250, 253)',
  // borderRight: `1px solid rgba(0,0,0,.12)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  flex: 1,
}
const CONTAINER: React.CSSProperties = { flex: 1, overflowY: 'auto' }
const QUESTION_ROW: React.CSSProperties = {
  position: 'relative',
  cursor: 'pointer',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 16,
  paddingLeft: 16,
  paddingRight: 16,
  borderBottom: `1px solid #EFF0F6`,
  minHeight: 50,
  backgroundColor: color.white,
}
const QUESTION_ROW_CONTAINER: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}
const HANDLE: React.CSSProperties = { color: color.shade20, alignSelf: 'center' }
const QUESTION_TEXT: React.CSSProperties = {
  textAlign: 'left',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '17px',
  overflowWrap: 'break-word',
  userSelect: 'none',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  maxWidth: 228,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingRight: 12,
}
const QUESTION_ROW_CONTENT: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  position: 'relative',
  justifyContent: 'space-between',
}
const QUESTION_ROW_TEXT: React.CSSProperties = {
  display: 'flex',
}
const FOOTER: React.CSSProperties = {
  alignItems: 'flex-end',
  display: 'flex',
  justifyContent: 'space-evenly',
  paddingTop: 24,
  width: '100%',
  backgroundColor: color.transparent,
}
const OPTIONAL_TEXT: React.CSSProperties = {
  fontStyle: 'italic',
  fontWeight: 400,
  fontSize: 14,
  color: color.shade80,
}
const SECTION_HEADER: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  color: color.shade80,
  marginRight: 4,
}

const LABEL: React.CSSProperties = {
  fontWeight: 500,
  fontSize: 14,
  color: color.shade80,
  marginRight: 12,
}
const SWITCH_ROW: React.CSSProperties = {
  display: 'flex',
  marginTop: 0,
  marginBottom: 24,
  alignItems: 'center',
  justifyContent: 'space-between',
}
const GENERATING_PANEL: React.CSSProperties = {
  flexDirection: 'row',
  alignItems: 'center',
  display: 'flex',
  border: `1px solid #EFF0F6`,
  justifyContent: 'space-between',
  padding: 12,
  borderRadius: 16,
  height: 74,
  width: 270,
  alignSelf: 'center',
  marginTop: 24,
  background: '#fff',
}
